import React, { useState, useEffect } from 'react';
import axios from 'axios';
// import useEmergenTTS from '../../hooks/useEmergenTTS'; // DISABLED - Voice removed to save costs
import { useLanguage } from '../../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIHelpAssistant = ({ isOpen, onClose }) => {
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [isListening, setIsListening] = useState(false);

  // Language context for multi-language support
  const { t, getLanguageCode, getLanguageName } = useLanguage();

  // Voice/TTS DISABLED to reduce costs
  // const {
  //   speakConversation,
  //   isSpeaking,
  //   stopSpeaking
  // } = useEmergenTTS();

  // Mock TTS functions (disabled to save costs)
  const speakConversation = async () => { /* TTS disabled */ };
  const isSpeaking = false;
  const stopSpeaking = () => { /* TTS disabled */ };

  // Clear conversation when modal opens
  useEffect(() => {
    if (isOpen) {
      setConversation([]);
      setInput('');
      
      // Multi-language welcome message
      const welcomeMessages = {
        'en': "Hi! I'm your AI assistant. I can help you with Edotask features, answer questions about using the app, or provide guidance on task management. How can I help you today?",
        'es': "¡Hola! Soy tu asistente de IA. Puedo ayudarte con las funciones de Edotask, responder preguntas sobre el uso de la aplicación o brindar orientación sobre la gestión de tareas. ¿Cómo puedo ayudarte hoy?",
        'fr': "Salut ! Je suis votre assistant IA. Je peux vous aider avec les fonctionnalités d'Edotask, répondre aux questions sur l'utilisation de l'application ou fournir des conseils sur la gestion des tâches. Comment puis-je vous aider aujourd'hui ?",
        'de': "Hallo! Ich bin Ihr KI-Assistent. Ich kann Ihnen bei Edotask-Funktionen helfen, Fragen zur App-Nutzung beantworten oder Anleitung zum Aufgabenmanagement geben. Wie kann ich Ihnen heute helfen?",
        'zh': "你好！我是您的AI助手。我可以帮助您了解Edotask功能，回答有关使用应用程序的问题，或提供任务管理指导。今天我能为您做些什么？"
      };
      
      const languageCode = getLanguageCode ? getLanguageCode() : 'en';
      const welcomeMessage = welcomeMessages[languageCode] || welcomeMessages['en'];
      
      setConversation([{
        type: 'ai',
        message: welcomeMessage,
        timestamp: new Date()
      }]);
      
      // Voice welcome message DISABLED to save costs
      // const timer = setTimeout(async () => {
      //   try {
      //     const langCode = getLanguageCode ? getLanguageCode() : 'en';
      //     console.log('🎤 AIHelpAssistant: Speaking welcome in language:', langCode);
      //     await speakConversation(welcomeMessage, 'nova', langCode);
      //   } catch (error) {
      //     console.error('Error speaking welcome message:', error);
      //   }
      // }, 500);
      
      // Cleanup function to prevent multiple calls (timer disabled)
      // return () => clearTimeout(timer);
    }
  }, [isOpen, getLanguageCode]); // Include getLanguageCode dependency

  // Cleanup when modal closes or component unmounts
  useEffect(() => {
    return () => {
      // Stop any ongoing speech when component unmounts or modal closes
      try {
        console.log('🔇 AIHelpAssistant: Stopping all voice operations on cleanup');
        stopSpeaking();
      } catch (error) {
        console.log('Error stopping speech on cleanup:', error);
      }
      
      // Stop speech recognition if active
      if (isListening) {
        try {
          // Stop any active speech recognition
          if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
          }
        } catch (error) {
          console.log('Error stopping speech recognition on cleanup:', error);
        }
        setIsListening(false);
      }
      
      // Clear any pending timers
      if (window.aiHelpProcessTimer) {
        clearTimeout(window.aiHelpProcessTimer);
        window.aiHelpProcessTimer = null;
      }
    };
  }, [isListening, stopSpeaking]);

  // Also cleanup when modal closes (isOpen changes to false)
  useEffect(() => {
    if (!isOpen) {
      try {
        console.log('🔇 AIHelpAssistant: Modal closed, stopping voice operations');
        stopSpeaking();
        if (window.speechSynthesis) {
          window.speechSynthesis.cancel();
        }
      } catch (error) {
        console.log('Error stopping speech on modal close:', error);
      }
    }
  }, [isOpen, stopSpeaking]);

  const handleAIHelp = async (question = input) => {
    if (!question.trim()) {
      const langCode = getLanguageCode ? getLanguageCode() : 'en';
      await speakConversation(t('pleaseAskQuestion'), 'nova', langCode);
      return;
    }

    // Add user message to conversation
    const userMessage = {
      type: 'user',
      message: question,
      timestamp: new Date()
    };
    setConversation(prev => [...prev, userMessage]);

    setIsProcessing(true);
    setInput('');

    try {
      const response = await axios.post(`${API}/ai/help`, {
        question: question,
        context: {
          app_name: 'Edotask',
          user_context: 'mobile_app',
          language: getLanguageName ? getLanguageName() : 'English',
          language_code: getLanguageCode ? getLanguageCode() : 'en'
        }
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        const aiResponse = response.data.data.answer_md || response.data.data.answer;
        
        // Add AI response to conversation
        const aiMessage = {
          type: 'ai',
          message: aiResponse,
          timestamp: new Date()
        };
        setConversation(prev => [...prev, aiMessage]);
        
        // Speak the response with high-quality Google TTS in user's language
        try {
          const langCode = getLanguageCode ? getLanguageCode() : 'en';
          const langName = getLanguageName ? getLanguageName() : 'English';
          console.log('🎤 Speaking AI Help response with Google TTS in', langName, 'code:', langCode);
          await speakConversation(aiResponse, 'nova', langCode);
        } catch (error) {
          console.error('Error speaking AI response:', error);
        }
        
      } else {
        const errorMessage = "I'm having trouble processing your question right now. Please try rephrasing it or ask something else.";
        setConversation(prev => [...prev, {
          type: 'ai',
          message: errorMessage,
          timestamp: new Date()
        }]);
        await speakConversation(errorMessage, 'nova');
      }
    } catch (error) {
      console.error('AI help error:', error);
      const errorMessage = "I'm experiencing technical difficulties. Please try again in a moment or contact support if the issue persists.";
      setConversation(prev => [...prev, {
        type: 'ai',
        message: errorMessage,
        timestamp: new Date()
      }]);
      await speakConversation(errorMessage, 'nova');
    } finally {
      setIsProcessing(false);
    }
  };

  const startVoiceInput = async () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      await speakConversation('Voice input is not supported in this browser', 'nova');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      console.log('🎤 Voice recognition started - user can speak now');
    };

    recognition.onresult = async (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      setIsListening(false);
      
      console.log('🎤 User said:', transcript);
      
      // Auto-process the voice question (handleAIHelp will add the message to conversation)
      handleAIHelp(transcript);
    };

    recognition.onerror = async (event) => {
      const langCode = getLanguageCode ? getLanguageCode() : 'en';
      await speakConversation(`${t('voiceRecognitionError')}: ${event.error}`, 'nova', langCode);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  const quickQuestions = [
    "How do I create a recurring task?",
    "How to add attachments to tasks?",
    "How does the calendar view work?",
    "How to manage contacts?",
    "What are the subscription features?"
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl h-[80vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center">
              <span className="text-white text-xl">🤖</span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">AI Help Assistant</h2>
              <p className="text-sm text-gray-600">Ask me anything about Edotask</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Conversation Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {conversation.map((message, index) => (
            <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl p-4 ${
                message.type === 'user' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                <div className="flex items-start space-x-2">
                  {message.type === 'ai' && (
                    <span className="text-lg">🤖</span>
                  )}
                  <div className="flex-1">
                    <p className="text-sm leading-relaxed">{message.message}</p>
                    <p className={`text-xs mt-2 ${
                      message.type === 'user' ? 'text-blue-200' : 'text-gray-500'
                    }`}>
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                  {message.type === 'user' && (
                    <span className="text-lg">👤</span>
                  )}
                </div>
              </div>
            </div>
          ))}

          {isProcessing && (
            <div className="flex justify-start">
              <div className="max-w-[80%] bg-gray-100 rounded-2xl p-4">
                <div className="flex items-center space-x-3">
                  <span className="text-lg">🤖</span>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                  <span className="text-sm text-gray-600">AI is thinking...</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quick Questions */}
        {conversation.length <= 1 && (
          <div className="px-6 pb-4">
            <p className="text-sm text-gray-600 mb-3">💡 Try asking about:</p>
            <div className="flex flex-wrap gap-2">
              {quickQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleAIHelp(question)}
                  className="px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 transition-colors"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-6 border-t border-gray-200">
          {/* Voice status indicators removed - microphone disabled */}
          
          <div className="flex space-x-3">
            <div className="flex-1 relative">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !isProcessing && handleAIHelp()}
                placeholder="Ask me about Edotask features, task management, or anything else..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                disabled={isProcessing}
              />
              
              {/* Voice Input Button - REMOVED to simplify UX and reduce complexity */}
            </div>
            
            <button
              onClick={() => handleAIHelp()}
              disabled={isProcessing || !input.trim() || isListening}
              className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                isProcessing || !input.trim() || isListening
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {isProcessing && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              <span>{isProcessing ? 'Processing...' : '🤖 Ask AI'}</span>
            </button>
          </div>
          
          <p className="text-xs text-gray-500 mt-2 text-center">
            {isSpeaking ? (
              <span className="flex items-center justify-center space-x-2 text-blue-600 font-medium">
                <svg className="w-4 h-4 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
                <span>🗣️ AI is speaking... Please wait to ask your next question</span>
              </span>
            ) : isListening ? (
              <span className="flex items-center justify-center space-x-2 text-red-600 font-medium">
                <svg className="w-4 h-4 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                </svg>
                <span>🎤 Listening... Speak now</span>
              </span>
            ) : (
              <span>💡 Type your question and press Enter or click Send to get help</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIHelpAssistant;